// Oracle Voice — The Archivist. A secure spoken intelligence channel.
// STT (mic → transcript) + TTS (briefing → speech). Voice is OFF by default:
// members intentionally enable it. Preferences sync to the account across
// devices; every spoken reply also renders as text.
import axios from "axios";
import { ORACLE_API, oracleAuthHeaders } from "./oracleClient";

const PREFS_KEY = "oracle_voice_prefs";

// Silent by default. Classic is the official voice once enabled.
const DEFAULTS = {
  enabled: false, preset: "classic", speed: 1.0,
  autoplay_briefing: false, greeted: false,
};

export const defaultVoicePrefs = () => {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { ...DEFAULTS };
};

const cachePrefs = (prefs) => {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch { /* ignore */ }
};

// Account-synced preferences (fall back to local cache when offline).
export const loadVoicePrefs = async () => {
  try {
    const r = await axios.get(`${ORACLE_API}/oracle/voice/prefs`,
      { headers: oracleAuthHeaders() });
    const prefs = { ...DEFAULTS, ...(r.data?.prefs || {}) };
    cachePrefs(prefs);
    return prefs;
  } catch { return defaultVoicePrefs(); }
};

export const saveVoicePrefs = async (patch, current) => {
  const next = { ...current, ...patch };
  cachePrefs(next);
  try {
    await axios.put(`${ORACLE_API}/oracle/voice/prefs`, patch,
      { headers: oracleAuthHeaders() });
  } catch { /* keep local cache; sync opportunistically next time */ }
  return next;
};

export const VOICE_OPTIONS = [
  { id: "classic", label: "Archivist (Classic)", desc: "Mid-Atlantic intelligence officer" },
  { id: "modern", label: "Archivist (Modern)", desc: "Neutral North American" },
  { id: "british", label: "Archivist (British)", desc: "Contemporary British" },
  { id: "text", label: "Text Only", desc: "No spoken briefings" },
];

// First-session greeting — played once, ever.
export const FIRST_GREETING =
  "Secure channel established. Welcome, Investigator. Archivist standing by.";

export const firstGreeting = (callsign) =>
  `Secure channel established. Welcome, ${callsign || "Investigator"}. Archivist standing by.`;

// Immersive session-state phrases (used subtly, never overused).
export const VOICE_STATES = {
  open: "SECURE CHANNEL ESTABLISHED",
  listening: "RECEIVING TRANSMISSION…",
  transcribing: "DECODING TRANSMISSION…",
  processing: "CROSS-REFERENCING ARCHIVE RECORDS…",
  speaking: "TRANSMITTING BRIEFING…",
  done: "TRANSMISSION COMPLETE",
};

// Subtle rotating operational presence (deterministic, never random).
export const PRESENCE_STATES = [
  "STANDING BY", "MONITORING", "REVIEWING INTELLIGENCE",
  "ARCHIVE SYNCHRONIZED", "CROSS-REFERENCING RECORDS",
];

// --- Speech-to-text: send a recorded blob, receive a transcript. ---
export const transcribeAudio = async (blob) => {
  const fd = new FormData();
  const ext = (blob.type || "").includes("mp4") ? "mp4"
    : (blob.type || "").includes("ogg") ? "ogg" : "webm";
  fd.append("audio", blob, `transmission.${ext}`);
  const r = await axios.post(`${ORACLE_API}/oracle/voice/transcribe`, fd, {
    headers: { ...oracleAuthHeaders() },
  });
  return (r.data?.text || "").trim();
};

// --- Text-to-speech: synthesize a spoken briefing (base64 mp3). ---
export const synthesizeSpeech = async (text, preset, speed) => {
  const r = await axios.post(`${ORACLE_API}/oracle/voice/speak`,
    { text, preset, speed }, { headers: { ...oracleAuthHeaders() } });
  return r.data; // { audio_base64, mime }
};

// --- Microphone recorder (MediaRecorder wrapper). ---
export const createRecorder = async () => {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
    : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
  const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
  const chunks = [];
  rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
  return {
    start: () => rec.start(),
    stop: () => new Promise((resolve) => {
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        resolve(new Blob(chunks, { type: mime || "audio/webm" }));
      };
      rec.stop();
    }),
    state: () => rec.state,
  };
};
